import React, {useEffect} from 'react';
import {useGetInviteData} from 'api/register';
import {PATH} from 'constants/common';
import {useLanguage} from 'context/language-context';
import UnauthorizedPageTitle from 'components/ui/UnauthorizedPageTitle/UnauthorizedPageTitle';
import {LayoutVariants} from 'components/layout/UnauthorisedLayout/UnauthorisedLayout';
import {Form as BSForm} from 'react-bootstrap';
import {createMaps} from 'helpers/common';
import {SEO_TEXTS} from 'constants/seo';
import {useResolveInviteStore} from 'components/pages/ResolveInvitePage/resolveInviteStore';
import AmnisLoader from 'components/ui/AmnisLoader/AmnisLoader';
import {translate} from 'helpers/localize';
import styles from './RegistrationPage.module.scss';
import RegisterForm from 'components/pages/RegistrationPage/RegisterForm';
import {useLocation} from 'react-router';
import AmnisButton from 'components/ui/AmnisButton/AmnisButton';
// TODO
//@ts-ignore
import {API_URL} from '../../../config';

interface IProps {
    variant: LayoutVariants;
}

export type VariantMapProperties = {
    welcomeText: React.ReactNode;
    pageTitle: string;
    loginPath: string;
    referringPartner?: string;
};

export const REGISTRATION_VARIANT_MAP = createMaps<
    Record<LayoutVariants, VariantMapProperties>
>({
    [LayoutVariants.DEFAULT]: {
        welcomeText: (
            <span data-isolate>
                <span className={styles.blueText}>Free</span>&nbsp;demo account
                in <span className={styles.blueText}>30 seconds</span>
            </span>
        ),
        pageTitle: 'Welcome to the Amnis Trading platform',
        loginPath: PATH.LOGIN,
    },
    [LayoutVariants.WIR]: {
        welcomeText: <span>Welcome to WIR FX Trading platform</span>,
        pageTitle: 'Welcome to the WIR FX Trading platform',
        loginPath: PATH.LOGIN_WIR,
    },
    [LayoutVariants.A352]: {
        welcomeText: <span>Welcome to A352 Trading platform</span>,
        pageTitle: 'Welcome to the A352 Trading platform',
        loginPath: PATH.LOGIN_A352,
        referringPartner: 'a352',
    },
    [LayoutVariants.BEXIO]: {
        welcomeText: <span>Welcome to amnis & bexio Pay</span>,
        pageTitle: 'Welcome to bexio amnis platform',
        loginPath: PATH.LOGIN,
        referringPartner: 'bexio',
    },
    [LayoutVariants.WIENER_PRIVATBANK]: {
        welcomeText: <span>Welcome to your amnis account</span>,
        pageTitle: 'Welcome to your amnis account',
        loginPath: PATH.LOGIN_WIENER_PRIVATBANK,
        referringPartner: 'wiener_privatbank',
    },
});

// It has to be 2 separate function, because of GoogleReCaptchaProvider
const RegistrationPage: React.FC<IProps> = ({variant}) => {
    const {pathname} = useLocation();
    const {selectedLanguageParams, updateLanguage, availableLanguages} =
        useLanguage();
    const hash = useResolveInviteStore(state => state.hash);
    const inviteType = useResolveInviteStore(state => state.type);

    const {data: inviteData, isLoading: isInviteDataLoading} = useGetInviteData(
        {hash, type: inviteType},
    );

    useEffect(() => {
        if (inviteData && inviteData.language) {
            const lang = availableLanguages.find(
                l => l.id === inviteData.language,
            );
            updateLanguage(lang?.code || 'en');
        }
    }, [availableLanguages, inviteData, updateLanguage]);

    if (isInviteDataLoading) {
        return <AmnisLoader />;
    }

    return (
        <>
            <UnauthorizedPageTitle
                pageTitle={
                    hash ? (
                        <>
                            <p data-notranslate>
                                {translate(
                                    '%{inviterName} from the company %{companyName} invited you to amnis!',
                                    {
                                        inviterName: inviteData?.inviterName,
                                        companyName: inviteData?.companyName,
                                    },
                                )}
                            </p>
                            <span data-isolate>
                                Sign up here in&nbsp;
                                <span className={styles.blueText}>
                                    30 seconds
                                </span>
                            </span>
                        </>
                    ) : (
                        REGISTRATION_VARIANT_MAP[variant].welcomeText
                    )
                }
                titleTag={
                    SEO_TEXTS[selectedLanguageParams.code]?.register?.title
                }
                metaTitle={
                    SEO_TEXTS[selectedLanguageParams.code]?.register?.title
                }
                metaDescription={
                    SEO_TEXTS[selectedLanguageParams.code]?.register
                        ?.description
                }
                metaKeywords={
                    SEO_TEXTS[selectedLanguageParams.code]?.register?.keywords
                }
            />
            {pathname === PATH.REGISTRATION_BEXIO_INIT ? (
                <>
                    <p className={styles.bexioText}>
                        Sign-up to amnis, connect your bexio account and
                        automate your bookkeeping for all card transactions.
                    </p>
                    <AmnisButton
                        onClick={() => {
                            window.open(
                                `${API_URL}/web_api/bexio/login`,
                                '_self',
                            );
                        }}
                    >
                        Register with bexio login
                    </AmnisButton>
                </>
            ) : (
                <RegisterForm variant={variant} inviteData={inviteData} />
            )}
            {variant === LayoutVariants.WIR ? (
                <BSForm.Group className="mt-2">
                    <BSForm.Label>Disclaimer</BSForm.Label>
                    <BSForm.Control
                        className={styles.textArea}
                        as="textarea"
                        name="disclaimer"
                        rows={2}
                        value={
                            'Dieses Angebot richtet sich ausschliesslich an in der Schweiz domizilierte KMU-Kunden der WIR Bank Genossenschaft.\n\nDie Bank behält sich das Recht vor, bestimmte Kundengruppen vom Angebot auszuschliessen. Mit der Registrierung und dem Zugang auf das von AMNIS betriebene FX Trading ist die/der Kunde/-in der WIR Bank Genossenschaft einverstanden, dass seine Daten an die AMNIS Treasury Services AGweitergegeben werden und im Rahmen der beanspruchten Dienstleistung bearbeitet werden können. Es gelten die Allgemeinen Geschäftsbedingungen der AMNIS Treasury Services AG. Alle Devisengeschäfte unterliegen Kursschwankungen. Dies erfordert eine entsprechende Risikotoleranz und -fähigkeit seitens der Kunden. Die Bank setzt voraus, dass sich die Kunden dessen bewusst und mit den Gegebenheiten der verschiedenen Devisengeschäfte (Spot, Forward), vertraut sind.'
                        }
                        disabled
                    />
                </BSForm.Group>
            ) : null}
        </>
    );
};

export default RegistrationPage;
